import type { FunctionComponent } from 'react';

import { hardRedirectTo } from '../../../utils';
import AuthLoginMethodLogoBitBucket from '../../auth/form/LoginMethodLogos/BitBucket';
import AuthLoginMethodLogoGitHub from '../../auth/form/LoginMethodLogos/GitHub';
import AuthLoginMethodLogoGitLab from '../../auth/form/LoginMethodLogos/GitLab';
import AuthLoginMethodLogoGoogle from '../../auth/form/LoginMethodLogos/Google';
import Button from '../../generic/Button';
import type { OAuthProvider } from '../../../../shared/types/routes/auth/v2';

import styles from './SocialLoginButtons.module.less';

type ProviderConfig = {
  label: string;
  url: string;
  // A no-prop function component (not `ComponentType`): its class-component branch resolves `render()` against
  // react-bootstrap's bundled `@types/react`, which trips TS2786 under the strict project config.
  Logo: FunctionComponent;
};

// Keyed by the wire `OAuthProvider` literal so this consumes `GET /api/auth/state`'s `oauthProviders` directly — no
// adapter. The URLs are the social-login entry points; the logo SVGs are reused.
const PROVIDER_CONFIG: Record<OAuthProvider, ProviderConfig> = {
  google: { label: 'Google', url: '/account/google', Logo: AuthLoginMethodLogoGoogle },
  github: { label: 'GitHub', url: '/account/github_login', Logo: AuthLoginMethodLogoGitHub },
  gitlab: { label: 'GitLab', url: '/account/gitlab_login', Logo: AuthLoginMethodLogoGitLab },
  bitbucket_cloud: { label: 'Bitbucket', url: '/account/bitbucket_cloud_login', Logo: AuthLoginMethodLogoBitBucket },
};

// Stable display order, independent of the order providers arrive in.
const PROVIDER_ORDER: OAuthProvider[] = ['google', 'github', 'gitlab', 'bitbucket_cloud'];

export type SocialLoginButtonsProps = {
  providers: OAuthProvider[];
};

/**
 * The "or sign in with" social-login buttons, driven directly by the wire `OAuthProvider[]`. Each button navigates to
 * its provider's entry point.
 */
const SocialLoginButtons = ({ providers }: SocialLoginButtonsProps) => {
  const enabledProviders = PROVIDER_ORDER.filter(provider => providers.includes(provider));

  if (enabledProviders.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.divider}>or sign in with</div>
      <ul className={styles.methods}>
        {enabledProviders.map(provider => {
          const { label, url, Logo } = PROVIDER_CONFIG[provider];
          return (
            <li key={provider} className={styles.method}>
              <Button
                type="secondary"
                size="large"
                title={`Sign in using ${label}`}
                onClick={() => hardRedirectTo(url)}
              >
                <Logo />
              </Button>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default SocialLoginButtons;
